import { useState } from 'react';
import { Shield, Users, UserCheck, KeyRound, LockKeyhole } from 'lucide-react';
import PermissionsTable from './Permissions/PermissionsTable';
import RolesTable from './Roles/RolesTable';
import AccessGroupsTable from './AccessGroups/AccessGroupsTable';
import GroupMembersManager from './Members/GroupMembersManager';
import { cn } from '@/lib/utils';

type TabType = 'permissions' | 'roles' | 'access-groups' | 'members';

const IAMDashboard = () => {
  const [activeTab, setActiveTab] = useState<TabType>('permissions');

  const tabs = [
    {
      id: 'permissions' as TabType,
      label: 'Permissions',
      icon: KeyRound,
      description: 'Manage system permissions',
    },
    {
      id: 'roles' as TabType,
      label: 'Roles',
      icon: LockKeyhole,
      description: 'Create roles and assign permissions',
    },
    {
      id: 'access-groups' as TabType,
      label: 'Access Groups',
      icon: UserCheck,
      description: 'Manage access groups and assign roles',
    },
    {
      id: 'members' as TabType,
      label: 'Group Members',
      icon: Users,
      description: 'Add and remove users from groups',
    },
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'permissions':
        return <PermissionsTable />;
      case 'roles':
        return <RolesTable />;
      case 'access-groups':
        return <AccessGroupsTable />;
      case 'members':
        return <GroupMembersManager />;
      default:
        return <PermissionsTable />;
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Shield className="mr-3 text-primary" size={32} />
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Identity & Access Management
            </h1>
            <p className="text-lg text-muted-foreground">
              Manage permissions, roles, access groups, and user memberships
            </p>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <div
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "p-4 rounded-lg cursor-pointer transition-all duration-200 border",
                  isActive
                    ? "ring-2 ring-primary bg-primary/10 border-primary"
                    : "hover:shadow-md border-border hover:bg-muted bg-background"
                )}
              >
                <div className="flex items-center mb-2">
                  <Icon
                    size={24}
                    className={isActive ? "text-primary" : "text-muted-foreground"}
                  />
                  <h3
                    className={cn(
                      "ml-3 font-semibold",
                      isActive ? "text-primary" : "text-foreground"
                    )}
                  >
                    {tab.label}
                  </h3>
                </div>
                <p
                  className={cn(
                    "text-sm",
                    isActive ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  {tab.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-border">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors",
                    isActive
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon size={16} className="mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {renderActiveTab()}
      </div>

      {/* Help Section */}
      <div
        className="mt-8 p-6 rounded-lg bg-primary/5 border border-border"
      >
        <h3 className="text-lg font-semibold mb-3 text-foreground">
          How to Use IAM
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
          <div>
            <h4 className="font-medium mb-2 text-foreground">
              1. Create Permissions
            </h4>
            <p>Start by creating permissions that define what actions users can perform in the system.</p>
          </div>
          <div>
            <h4 className="font-medium mb-2 text-foreground">
              2. Create Roles
            </h4>
            <p>Group permissions into roles to make management easier and more organized.</p>
          </div>
          <div>
            <h4 className="font-medium mb-2 text-foreground">
              3. Create Access Groups
            </h4>
            <p>Create access groups and assign roles to them. Set priority levels for access control.</p>
          </div>
          <div>
            <h4 className="font-medium mb-2 text-foreground">
              4. Manage Members
            </h4>
            <p>Add users to access groups to grant them the permissions defined by the group's roles.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IAMDashboard;
